const { default: def } = require("ajv/dist/vocabularies/applicator/additionalItems");

const supportedLocales = {
    GB: {
        currency: "GBP",
        currencySymbol: "£",
        rewardsLower: {
            amount: 24,
            discount: 20,
            couponId: "GC-REWARDS-20"
        },
        rewardsMid: {
            amount: 45,
            discount: 30,
            couponId: "GC-REWARDS-30"
        },
        rewardsHigh: {
            amount: 100,
            discount: 40,
            couponId: ""
        },
       
    },
    US: {
        currency: "USD",
        currencySymbol: "$",
        rewardsLower: {
            amount: 35,
            discount: 20,
            couponId: "GC-REWARDS-20"
        },
        rewardsMid: {
            amount: 65,
            discount: 30,
            couponId: "GC-REWARDS-30"
        },
        rewardsHigh: {
            amount: 150,
            discount: 40,
            couponId: ""
        },
    },
    AU: {
        currency: "AUD",
        currencySymbol: "$",
        rewardsLower: {
            amount: 48,
            discount: 20,
            couponId: "GC-REWARDS-20"
        },
        rewardsMid: {
            amount: 86,
            discount: 30,
            couponId: "GC-REWARDS-30"
        },
        rewardsHigh: {
            amount: 200,
            discount: 40,
            couponId: ""
        },
    }
};

const getCurrency = (countryCode) => {
    let locale = supportedLocales[countryCode];
    if (locale == null) {
        return supportedLocales.GB.currency;
    } else {
        return locale.currency;
    }
}


 const getCurrencySymbol = (countryCode) => {
    let locale = supportedLocales[countryCode];
    if (locale == null) {
        return supportedLocales.GB.currencySymbol;
    } else {
        return locale.currencySymbol;
    }
}

const getGamePricing = (countryCode, game, old = false) => {
    if (!game) {
        console.warn('getGamePricing: game is undefined');
        return { raw: 0, display: '£0.00', currency: '£' };
    }
    const priceSource = old ? game.oldPricing : game.pricing;
    return priceSource?.[countryCode?.toLowerCase()] ?? priceSource?.gb ?? { raw: 0, display: '£0.00', currency: '£' };
}

// Cross-region twin discount helpers. The catalog endpoint attaches a
// crossRegionDiscount block to products the authenticated user qualifies for
// (i.e. they own the regional twin). All UI that renders a price should prefer
// getEffectiveGamePricing over getGamePricing so the discount is reflected.

const hasCrossRegionDiscount = (game) => !!game?.crossRegionDiscount?.effectivePricing;

const getCrossRegionDiscount = (game) => game?.crossRegionDiscount ?? null;

const getEffectiveGamePricing = (countryCode, game) => {
    if (!game) {
        console.warn('getEffectiveGamePricing: game is undefined');
        return { raw: 0, display: '£0.00', currency: '£' };
    }
    if (hasCrossRegionDiscount(game)) {
        const effective = game.crossRegionDiscount.effectivePricing;
        return effective[countryCode?.toLowerCase()] ?? effective.gb ?? getGamePricing(countryCode, game);
    }
    return getGamePricing(countryCode, game);
}

// Companion offer helpers. The catalog endpoint always returns a companionOffer
// block for products that have a regional twin, regardless of authentication.
// Use these to show "buy both and save" messaging to all users.
// Priority: if the user already owns the companion (crossRegionDiscount exists),
// show that instead — the companion offer is for users who don't own it yet.

const hasCompanionOffer = (game) => !!game?.companionOffer?.companionStoreGameId;

const getCompanionOffer = (game) => game?.companionOffer ?? null;

const shouldShowCompanionOffer = (game) => hasCompanionOffer(game) && !hasCrossRegionDiscount(game);

// Cart companion discount: checks if a game's companion is also in the cart.
// When both twins are in the cart and both have companionOffer (bidirectional),
// only the cheaper game gets the discount. If same price, the one appearing
// later in the cart gets it (so the "original" stays full price).
// Returns { discountPercent, companionName, discountedQty } or null.
// discountedQty = number of units that get the discount (capped to companion's quantity).
const getCartCompanionDiscount = (game, cartItems, allGames) => {
    if (!game?.companionOffer) return null;
    const offer = game.companionOffer;

    // Find the companion game and its cart item
    let companionGame = null;
    let companionCartItem = null;
    const companionInCart = cartItems.some(item => {
        const cartGame = allGames.find(g => g.productId === item.productId || g.gameId === item.id);
        if (cartGame && (cartGame.gameId === offer.companionStoreGameId || cartGame.storeGameId === offer.companionStoreGameId)) {
            companionGame = cartGame;
            companionCartItem = item;
            return true;
        }
        return false;
    });
    if (!companionInCart || !companionGame) return null;

    // If companion also has a companionOffer (bidirectional), only discount one.
    // The more expensive game stays full price; the cheaper one gets the discount.
    if (companionGame.companionOffer) {
        const myPrice = game.pricing?.gb?.raw ?? 0;
        const theirPrice = companionGame.pricing?.gb?.raw ?? 0;
        if (myPrice > theirPrice) return null; // I'm more expensive — no discount on me
        if (myPrice === theirPrice) {
            // Same price — use gameId alphabetical order as tiebreaker
            const myId = game.gameId || game.storeGameId || '';
            const theirId = companionGame.gameId || companionGame.storeGameId || '';
            if (myId < theirId) return null; // I sort first — no discount on me
        }
    }

    // Quantity matching: discount only as many units as the companion has in the cart
    const myCartItem = cartItems.find(item => item.productId === game.productId || item.id === game.gameId);
    const myQty = myCartItem?.quantity || 1;
    const companionQty = companionCartItem?.quantity || 1;
    const discountedQty = Math.min(myQty, companionQty);

    return { discountPercent: offer.discountPercent, companionName: offer.companionName, discountedQty };
};

// Get the effective total price for a cart line, applying companion discount with quantity matching.
// If item qty exceeds companion qty, only some units are discounted (blended total).
// Works for both owned-companion and cart-companion scenarios.
const getCartEffectiveGamePricing = (countryCode, game, cartItems, allGames) => {
    if (hasCrossRegionDiscount(game)) {
        // User owns the companion — discount is quantity-matched too.
        // Discounted copies = 1 (owned) + any extra copies of the twin in the cart.
        const discount = getCrossRegionDiscount(game);
        const basePricing = getGamePricing(countryCode, game);
        const myCartItem = cartItems.find(item => item.productId === game.productId || item.id === game.gameId);
        const totalQty = myCartItem?.quantity || 1;

        if (totalQty <= 1) {
            // Single unit — just use the pre-calculated effective price
            return getEffectiveGamePricing(countryCode, game);
        }

        // Find the owned twin's copies in the cart
        const offer = getCompanionOffer(game);
        let twinCartQty = 0;
        if (offer) {
            const twinCartItem = cartItems.find(item => {
                const cartGame = allGames.find(g => g.productId === item.productId || g.gameId === item.id);
                return cartGame && (cartGame.gameId === offer.companionStoreGameId || cartGame.storeGameId === offer.companionStoreGameId);
            });
            twinCartQty = twinCartItem?.quantity || 0;
        }

        const totalTwinCopies = 1 + twinCartQty; // 1 owned + extras in cart
        const discountedQty = Math.min(totalQty, totalTwinCopies);
        const fullPriceQty = totalQty - discountedQty;

        const unitPrice = basePricing.raw;
        // Mirror Stripe's percent-off coupon: round the discount in the smallest
        // currency unit, then subtract from the unit price. Computing the
        // discounted price directly and rounding (e.g. round(7.99 * 0.5) → 4.00)
        // disagrees with Stripe by 1p whenever the discount lands on a half-cent.
        const unitPriceCents = Math.round(unitPrice * 100);
        const discountCents = Math.round(unitPriceCents * discount.percent / 100);
        const discountedUnitPrice = (unitPriceCents - discountCents) / 100;

        const blendedTotal = (discountedQty * discountedUnitPrice) + (fullPriceQty * unitPrice);
        const blendedPerUnit = Math.round(blendedTotal / totalQty * 100) / 100;

        return {
            raw: blendedPerUnit,
            display: `${basePricing.currency}${blendedPerUnit.toFixed(2)}`,
            currency: basePricing.currency
        };
    }
    const cartDiscount = getCartCompanionDiscount(game, cartItems, allGames);
    if (cartDiscount) {
        const basePricing = getGamePricing(countryCode, game);
        const unitPrice = basePricing.raw;
        // Stripe-aligned discount math — see comment in the cross-region branch above.
        const unitPriceCents = Math.round(unitPrice * 100);
        const discountCents = Math.round(unitPriceCents * cartDiscount.discountPercent / 100);
        const discountedUnitPrice = (unitPriceCents - discountCents) / 100;

        const myCartItem = cartItems.find(item => item.productId === game.productId || item.id === game.gameId);
        const totalQty = myCartItem?.quantity || 1;
        const discountedQty = cartDiscount.discountedQty;
        const fullPriceQty = totalQty - discountedQty;

        // Blended total for the line, divided back to a per-unit average
        const blendedTotal = (discountedQty * discountedUnitPrice) + (fullPriceQty * unitPrice);
        const blendedPerUnit = Math.round(blendedTotal / totalQty * 100) / 100;

        return {
            raw: blendedPerUnit,
            display: `${basePricing.currency}${blendedPerUnit.toFixed(2)}`,
            currency: basePricing.currency
        };
    }
    return getGamePricing(countryCode, game);
};

const getRewardsLower = (countryCode) => {
    let locale = supportedLocales[countryCode];
    if (locale == null) {
        return supportedLocales.GB.rewardsLower;
    } else {
        return locale.rewardsLower;
    }
}

const getRewardsMid = (countryCode) => {
    let locale = supportedLocales[countryCode];
    if (locale == null) {
        return supportedLocales.GB.rewardsMid;
    } else {
        return locale.rewardsMid;
    }
}

const getRewardsHigh = (countryCode) => {
    let locale = supportedLocales[countryCode];
    if (locale == null) {
        return supportedLocales.GB.rewardsHigh;
    } else {
        return locale.rewardsHigh;
    }
}


export default {
    getCurrency,
    getCurrencySymbol,
    getGamePricing,
    getEffectiveGamePricing,
    hasCrossRegionDiscount,
    getCrossRegionDiscount,
    hasCompanionOffer,
    getCompanionOffer,
    shouldShowCompanionOffer,
    getCartCompanionDiscount,
    getCartEffectiveGamePricing,
    getRewardsLower,
    getRewardsMid,
    getRewardsHigh
}